<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-[925px]">
            <DialogHeader>
                <DialogTitle>DNS info</DialogTitle>
            </DialogHeader>
            <Table>
                <TableBody>
                    <TableRow>
                        <TableHead class="w-[150px]">Domain Name</TableHead>
                        <TableCell>{{ dns.domain_name }}</TableCell>
                        <TableHead>Registrant Name</TableHead>
                        <TableCell>{{ dns.registrant_name || '--' }}</TableCell>
                        <TableHead>Registrant ID</TableHead>
                        <TableCell class="w-[150px]">{{ dns.registrant_id || '--' }}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableHead>Expires On</TableHead>
                        <TableCell>{{ dns.expiration_date_format_b || '--' }}</TableCell>
                        <TableHead class="w-[150px]">Registered On</TableHead>
                        <TableCell class="w-[150px]">{{
                            dns.creation_date_format_b || '--'
                        }}</TableCell>
                        <TableHead class="w-[150px]">Updated On</TableHead>
                        <TableCell>{{ dns.updated_date_format_b || '--' }}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableHead>Name Servers</TableHead>
                        <TableCell>{{ dns.name_servers.join(', ') }}</TableCell>
                        <TableHead>Status</TableHead>
                        <TableCell colspan="3">{{ dns.states.join(', ') }}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableHead>Registrar</TableHead>
                        <TableCell>{{ dns.registrar }}</TableCell>
                        <TableHead>Whois Server</TableHead>
                        <TableCell colspan="3">{{ dns.whois_server }}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <div v-if="records">
                <div class="flex items-center justify-between mb-2">
                    <h6 class="text-sm font-semibold">DNS Records</h6>
                    <div class="flex items-center space-x-2">
                        <Button
                            variant="ghost"
                            class="bg-accent hover:bg-blue-100"
                            size="sm"
                            @click="exportDNSRecordToCsv"
                        >
                            <Loader
                                v-if="exporting"
                                class="h-4 w-4 mr-2 animate-spin"
                                :stroke-width="1"
                            />
                            <DownloadIcon v-else class="h-4 w-4 mr-2" :stroke-width="1" />
                            Export
                        </Button>
                        <Button
                            variant="ghost"
                            class="bg-accent hover:bg-blue-100"
                            size="sm"
                            @click="$emit('refresh')"
                        >
                            <Loader
                                v-if="loading"
                                class="h-4 w-4 mr-2 animate-spin"
                                :stroke-width="1"
                            />
                            <RefreshCwIcon v-else class="h-4 w-4 mr-2" :stroke-width="1" />
                            Refresh
                        </Button>
                    </div>
                </div>
                <div class="overflow-auto max-h-96">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead class="sticky top-0 bg-white">Type</TableHead>
                                <TableHead class="sticky top-0 bg-white">Name</TableHead>
                                <TableHead class="sticky top-0 bg-white">Content</TableHead>
                                <TableHead class="sticky top-0 bg-white">TTL</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow v-for="record in records" :key="record.id">
                                <TableCell>{{ record.type }}</TableCell>
                                <TableCell>{{ record.name }}</TableCell>
                                <TableCell>{{ record.content }}</TableCell>
                                <TableCell>{{ record.ttl }}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { DNS } from '@/lib/types'
import httpClient from '@/utils/httpClient'
import { DownloadIcon } from '@radix-icons/vue'
import { RefreshCwIcon } from 'lucide-vue-next'
import { Loader } from 'lucide-vue-next'
import { computed, ref } from 'vue'

const props = defineProps<{
    dns: DNS
    loading: boolean
    nameserver: string
}>()

defineEmits(['refresh'])

const open = defineModel()

const records = computed(() => {
    return props.dns.records
})

const exporting = ref(false)

const exportDNSRecordToCsv = async () => {
    try {
        exporting.value = true
        const { data } = await httpClient.get(
            `dns/${props.nameserver}/${props.dns.domain_name}/records/export/csv`,
            {
                responseType: 'blob',
            }
        )

        const href = window.URL.createObjectURL(data)

        const anchorElement = document.createElement('a')

        anchorElement.href = href
        anchorElement.download = `${props.dns.domain_name}-dns-records.csv`

        document.body.appendChild(anchorElement)
        anchorElement.click()

        document.body.removeChild(anchorElement)
        window.URL.revokeObjectURL(href)
    } finally {
        exporting.value = false
    }
}
</script>
